import { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import {
  Button,
  Space,
  Row,
  Col,
  Typography,
  Card,
  Table,
  Form,
  Switch,
  Modal,
  message,
} from 'antd'
import {
  ActionButton,
  AlertModal,
  FileEditModal,
  ModuleTopHeading,
} from '../../components'
import { actionsApi, ApiCalls, TableLoader } from '../../shared'
import { DeleteOutlined, EditOutlined } from '@ant-design/icons'
import {
  AddEditBoxModal,
  AddEditPortalModal,
  Filter,
  ImportDevices,
} from '../../components/BoxManagementGo'
import { useTranslation } from 'react-i18next'

const { Text } = Typography

const BoxManagementGo = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const {
    data,
    loading,
    pagination,
    portals,
    portalsLoading,
    portalsPagination,
  } = useSelector((state) => state.boxManagementGo)

  const [form] = Form.useForm()
  const [visible, setVisible] = useState(false)
  const [visibleDeleteModal, setVisibleDeleteModal] = useState(false)
  const [editbox, setEditBox] = useState(null)
  const [editmodal, setEditModal] = useState(false)
  const [deletebox, setDeleteBox] = useState()

  // Free TV state
  const [freeTvCategories, setFreeTvCategories] = useState([])
  const [initialCategories, setInitialCategories] = useState([])
  const [freeTvLoading, setFreeTvLoading] = useState(false)

  // Channel modal state
  const [channelModalVisible, setChannelModalVisible] = useState(false)
  const [currentChannels, setCurrentChannels] = useState([])
  const [initialChannels, setInitialChannels] = useState([])
  const [currentCategoryName, setCurrentCategoryName] = useState('')

  // Load data on mount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    portalCall()
    fetchFreeTv()
  }, [])

  // Box list
  const call = (pageNo = 1, pageSize = 10) => {
    dispatch(
      actionsApi.viewAllAndroidBoxesGo({
        ...form.getFieldsValue(),
        pageNo,
        pageSize,
      })
    )
  }

  // Portal list
  const portalCall = (pageNo = 1, pageSize = 10) => {
    dispatch(actionsApi.viewAllPortalsIptvGo({ pageNo, pageSize }))
  }

  // --- FreeTV handlers ---
  const fetchFreeTv = async () => {
    setFreeTvLoading(true)
    try {
      const res = await ApiCalls.boxManagementGo.listLive()
      if (res.success) {
        setFreeTvCategories(res.categories)
        setInitialCategories(JSON.parse(JSON.stringify(res.categories)))
      } else {
        message.error(res.message || 'Free TV listelenemedi.')
      }
    } catch {
      message.error('Free TV fetch hatası')
    }
    setFreeTvLoading(false)
  }

  // toggle only local state
  const onCategoryToggle = (id, active) => {
    setFreeTvCategories((cats) =>
      cats.map((c) => (c.id === id ? { ...c, active: active ? 1 : 0 } : c))
    )
  }

  // Save categories to backend
  const saveCategories = async () => {
    const updates = freeTvCategories.filter(
      (c, idx) => c.active !== initialCategories[idx].active
    )
    for (let c of updates) {
      await ApiCalls.boxManagementGo.updateCategoryStatus(c.id, {
        active: c.active,
      })
    }
    message.success('Kategoriler kaydedildi.')
    fetchFreeTv()
  }

  // Channel modal functions
  const openChannelModal = (record) => {
    setCurrentCategoryName(record.name)
    setCurrentChannels(record.channels || [])
    setInitialChannels(JSON.parse(JSON.stringify(record.channels || [])))
    setChannelModalVisible(true)
  }

  // toggle only local channels state
  const onChannelToggle = (id, active) => {
    setCurrentChannels((chs) =>
      chs.map((c) => (c.id === id ? { ...c, active: active ? 1 : 0 } : c))
    )
  }

  // Save channel changes
  const saveChannels = async () => {
    const updates = currentChannels.filter(
      (ch, idx) => ch.active !== initialChannels[idx].active
    )
    for (let ch of updates) {
      await ApiCalls.boxManagementGo.updateChannelStatus(ch.id, {
        active: ch.active,
      })
    }
    message.success('Kanallar kaydedildi.')
    setChannelModalVisible(false)
    fetchFreeTv()
  }

  // Delete handlers
  const deleteBox = async (ID) =>
    (await ApiCalls.boxManagementGo.deleteBox(ID)) ? 1 : 0
  const deletePortal = async (ID) =>
    (await ApiCalls.boxManagementGo.deletePortal(ID)) ? 1 : 0

  // Table columns for Boxes
  const boxColumns = [
    {
      title: <Text>{t('Serial #')}</Text>,
      dataIndex: 'serialNo',
      key: 'serialNo',
    },
    {
      title: <Text>{t('Mac Address')}</Text>,
      dataIndex: 'macAddress',
      key: 'macAddress',
      render: (col) => <Text className="brand-color">{col}</Text>,
    },
    {
      title: <Text>{t('Added By')}</Text>,
      dataIndex: 'addedFor',
      key: 'addedFor',
      render: (col) => (
        <Text>
          {col === 1
            ? t('Master Admin')
            : col === 2
            ? t('Master Vendor')
            : t('Vendor user')}
        </Text>
      ),
    },
    {
      title: <Text>{t('Action')}</Text>,
      key: 'action',
      fixed: 'right',
      width: 100,
      render: (_, row) => (
        <Space>
          <ActionButton
            title={t('Edit Box')}
            icon={<EditOutlined className="green-color" />}
            onClick={() => {
              setVisible(1)
              setEditBox(row)
            }}
          />
          <ActionButton
            title={t('Delete Box')}
            icon={<DeleteOutlined className="danger-color" />}
            onClick={() => {
              setVisibleDeleteModal(1)
              setDeleteBox({ id: row.id, name: row.serialNo })
            }}
            danger
          />
        </Space>
      ),
    },
  ]

  // Table columns for Portals
  const portalsColumns = [
    {
      title: <Text>{t('Name')}</Text>,
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: <Text>{t('Url')}</Text>,
      dataIndex: 'url',
      key: 'url',
      render: (col) => <Text className="brand-color">{col}</Text>,
    },
    {
      title: <Text>{t('Action')}</Text>,
      key: 'action',
      fixed: 'right',
      width: 100,
      render: (_, row) => (
        <Space>
          <ActionButton
            title={t('Edit Portal')}
            icon={<EditOutlined className="green-color" />}
            onClick={() => {
              setVisible(2)
              setEditBox(row)
            }}
          />
          <ActionButton
            title={t('Delete Portal')}
            icon={<DeleteOutlined className="danger-color" />}
            onClick={() => {
              setVisibleDeleteModal(2)
              setDeleteBox({ id: row.id, name: row.name })
            }}
            danger
          />
        </Space>
      ),
    },
  ]

  // Table columns for FreeTV Categories (name is clickable)
  const freeTvCatColumns = [
    {
      title: <Text>{t('Category')}</Text>,
      dataIndex: 'name',
      key: 'name',
      render: (name, record) => (
        <a style={{ cursor: 'pointer' }} onClick={() => openChannelModal(record)}>
          {name}
        </a>
      ),
    },
    {
      title: <Text>{t('Active')}</Text>,
      dataIndex: 'active',
      key: 'active',
      render: (val, rec) => (
        <Switch
          checked={val === 1}
          onChange={(v) => onCategoryToggle(rec.id, v)}
        />
      ),
    },
  ]

  // Table columns for Channels in modal
  const channelModalColumns = [
    {
      title: <Text>{t('Name')}</Text>,
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: <Text>{t('Active')}</Text>,
      dataIndex: 'active',
      key: 'active',
      render: (val, rec) => (
        <Switch
          checked={val === 1}
          onChange={(v) => onChannelToggle(rec.id, v)}
        />
      ),
    },
  ]

  return (
    <div>
      {/* Box & Portal Management */}
      <Card className="border0">
        <Row gutter={[24, 24]}>
          <Col span={24}>
            <ModuleTopHeading
              name={t('Box Management Iptv Go')}
              onClick={() => setVisible(1)}
            />
          </Col>
          <Col span={24}>
            <Filter form={form} />
            <Table
              size="large"
              columns={boxColumns}
              dataSource={data}
              rowKey="id"
              className="table-cover"
              pagination={{
                hideOnSinglePage: true,
                total: pagination.totalRecords,
                pageSize: pagination.pageSize,
                current: pagination.pageNo,
                pageSizeOptions: ['10', '20', '50', '100'],
                onChange: call,
                showTotal: (total) => <Button>{t('Total')}: {total}</Button>,
              }}
              loading={{ ...TableLoader, spinning: loading }}
            />
            <ImportDevices callback={call} />
          </Col>
          <Col span={24}>
            <ModuleTopHeading name={t('Portal Management GO IPTV')} />
          </Col>
          <Col span={24}>
            <Table
              size="large"
              columns={portalsColumns}
              dataSource={portals}
              rowKey="id"
              className="table-cover"
              pagination={{
                hideOnSinglePage: true,
                total: portalsPagination.totalRecords,
                pageSize: portalsPagination.pageSize,
                current: portalsPagination.pageNo,
                pageSizeOptions: ['10', '20', '50', '100'],
                onChange: portalCall,
                showTotal: (total) => <Button>{t('Total')}: {total}</Button>,
              }}
              loading={{ ...TableLoader, spinning: portalsLoading }}
            />
          </Col>
        </Row>
      </Card>

      {/* Free TV Management */}
      <Card className="border0" style={{ marginTop: 24 }}>
        <Row gutter={[24, 24]}>
          <Col span={24}>
            <ModuleTopHeading name={t('Free TV Management')} />
          </Col>
          <Col span={24}>
            <Table
              size="small"
              columns={freeTvCatColumns}
              dataSource={freeTvCategories}
              rowKey="id"
              pagination={false}
              loading={freeTvLoading}
            />
          </Col>
          <Col span={24} style={{ textAlign: 'right' }}>
            <Button type="primary" onClick={saveCategories}>
              {t('Save Categories')}
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Channels Modal */}
      <Modal
        title={t('Channels for {{category}}', { category: currentCategoryName })}
        open={channelModalVisible}
        onCancel={() => setChannelModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setChannelModalVisible(false)}>
            {t('Cancel')}
          </Button>,
          <Button key="save" type="primary" onClick={saveChannels}>
            {t('Save Channels')}
          </Button>,
        ]}
        width={600}
      >
        <Table
          size="small"
          columns={channelModalColumns}
          dataSource={currentChannels}
          rowKey="id"
          pagination={false}
        />
      </Modal>

      {/* Other Modals */}
      <AddEditBoxModal
        open={visible === 1}
        editbox={editbox}
        getAllApplications={call}
        onClose={() => {
          setVisible(false)
          setEditBox(null)
        }}
      />
      <AddEditPortalModal
        open={visible === 2}
        editbox={editbox}
        getAllApplications={portalCall}
        onClose={() => {
          setVisible(false)
          setEditBox(null)
        }}
      />
      <FileEditModal editmodal={editmodal} onClose={() => setEditModal(false)} />
      <AlertModal
        open={visibleDeleteModal}
        row={deletebox}
        removeRow={visibleDeleteModal === 1 ? deleteBox : deletePortal}
        endCall={visibleDeleteModal === 1 ? call : portalCall}
        onClose={() => {
          setVisibleDeleteModal(false)
          setDeleteBox(null)
        }}
      />
    </div>
  )
}

export { BoxManagementGo }


